import React, { useState, useCallback, useEffect } from 'react';
import { IUserJson } from '@scale/shared/types/user';
import { Button, Modal, TextField, TextArea, Typography } from '@scale/scaleui';
import { useCreateWebinarSession } from '../hooks/useCreateWebinarSessionStore';
import { Scheduler } from './Scheduler';
import { People } from './People';
import { Communications } from './Communications';
import SelectProject from 'react-scale/corp/components/UI/SelectProject';
import { toast } from 'react-toastify';
import { WebinarErrorMessages } from 'react-scale/components/WebinarSessions/constants';

interface CreateSessionModalProps {
  user: IUserJson;
  showModal: boolean;
  toggleModal: () => void;
  onSuccess: () => void;
  projectId?: string;
}

export const CreateSessionModal: React.FC<CreateSessionModalProps> = ({
  showModal,
  toggleModal,
  onSuccess,
  projectId,
}) => {
  const {
    name,
    setName,
    eventStart,
    setEventStart,
    duration,
    setDuration,
    occurrences,
    setOccurrences,
    description,
    setDescription,
    rsvpMax,
    setRsvpMax,
    minAttendanceDuration,
    setMinAttendanceDuration,
    setProject,
    invitedUsers,
    setInvitedUsers,
    audience,
    setAudience,
    audienceBucket,
    setAudienceBucket,
    invitedWorkerTeams,
    setInvitedWorkerTeams,
    invitedQualifications,
    setInvitedQualifications,
    invitedSquads,
    setInvitedSquads,
    hostedBy,
    setHostedBy,
    createSessions,
    getErrors,
    sessionType,
    setSessionType,
    setIsProjectCreation,
  } = useCreateWebinarSession();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [projectOption, setProjectOption] = useState<{ label: string; value: string } | null>(null);

  const handleCreateSessions = useCallback(() => {
    (async () => {
      try {
        setIsLoading(true);
        await createSessions();
        setIsLoading(false);
        onSuccess();
      } catch (e) {
        toast(`Failed to create sessions: ${e.message}`, {
          type: 'error',
          autoClose: 2000,
          position: 'top-right',
        });
        toggleModal();
      }
    })();
  }, [createSessions, onSuccess, toggleModal]);

  useEffect(() => {
    if (projectId) {
      setProject(projectId);
      setIsProjectCreation(true);
    }
  }, [projectId, setProject, setIsProjectCreation]);

  // Only set projects via the select if no prop is provided
  useEffect(() => {
    if (!projectId) {
      setProject(projectOption ? projectOption.value : null);
    }
  }, [projectId, projectOption, setProject]);

  return (
    <Modal
      title="Create a New Session(s)"
      open={showModal}
      onClose={toggleModal}
      actions={
        <div className="flex items-center gap-2 justify-end">
          <Button onClick={toggleModal} variant="secondary">
            Cancel
          </Button>
          <Button disabled={isLoading || !!getErrors().length} onClick={handleCreateSessions}>
            {isLoading ? 'Creating...' : 'Create Session(s)'}
          </Button>
        </div>
      }
    >
      <div className="scaleui scale-tailwind flex flex-col gap-2">
        <TextField
          label="Name"
          placeholder="Enter session name"
          value={name ?? ''}
          onChange={e => setName(e.target.value)}
        />
        <Scheduler
          recurring={sessionType}
          setRecurring={setSessionType}
          eventStart={eventStart}
          setEventStart={setEventStart}
          duration={duration}
          setDuration={setDuration}
          occurrences={occurrences}
          setOccurrences={setOccurrences}
        />
        <People
          invitedUsers={invitedUsers}
          setInvitedUsers={setInvitedUsers}
          audience={audience}
          setAudience={setAudience}
          audienceBucket={audienceBucket}
          setAudienceBucket={setAudienceBucket}
          invitedWorkerTeams={invitedWorkerTeams}
          setInvitedWorkerTeams={setInvitedWorkerTeams}
          invitedQualifications={invitedQualifications}
          setInvitedQualifications={setInvitedQualifications}
          invitedSquads={invitedSquads}
          setInvitedSquads={setInvitedSquads}
          hostedBy={hostedBy}
          setHostedBy={setHostedBy}
          projectId={projectId}
        />
        <TextField
          label="Max RSVPs (optional)"
          type="number"
          className="w-[180px]"
          value={rsvpMax ?? ''}
          onChange={e => setRsvpMax(Number(e.target.value))}
          min={1}
          step={1}
        />
        <TextField
          label="Min Attendance Duration in Minutes (optional)"
          type="number"
          className="w-[360px]"
          value={minAttendanceDuration ?? ''}
          onChange={e => setMinAttendanceDuration(Number(e.target.value))}
          min={0}
          max={duration ?? 0}
          step={1}
          error={(minAttendanceDuration ?? 0) > (duration ?? 0)}
        />
        <Communications />
        <Typography.Subtitle3 className="text-neutral-600">
          Project{projectId ? '' : ' (optional)'}
        </Typography.Subtitle3>
        {projectId ? (
          <Typography.Paragraph3>{projectId}</Typography.Paragraph3>
        ) : (
          <SelectProject
            onChange={(projectOption: { label: string; value: string } | null) => {
              setProjectOption(projectOption);
            }}
            value={projectOption}
          />
        )}
        <TextArea
          label="Description (optional)"
          placeholder="Enter session description"
          value={description ?? ''}
          onChange={e => setDescription(e.target.value)}
        />
        {!!getErrors().length && (
          <Typography.Subtitle3 className="text-neutral-600">Errors</Typography.Subtitle3>
        )}
        <div className="flex flex-col gap-1">
          {getErrors().map(error => (
            <Typography.Paragraph2 key={error} className="text-danger-500">
              {WebinarErrorMessages[error]}
            </Typography.Paragraph2>
          ))}
        </div>
      </div>
    </Modal>
  );
};
